import * as React from 'react';
import { useState } from 'react';
import Box from '@mui/material/Box';
import InboxIcon from '@mui/icons-material/Inbox';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import PublicIcon from '@mui/icons-material/Public';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TimelineIcon from '@mui/icons-material/Timeline';
import LinkIcon from '@mui/icons-material/Link';
import ActivityIcon from '@mui/icons-material/BarChart';
import RecyclingIcon from '@mui/icons-material/Recycling';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import InsightsIcon from '@mui/icons-material/Insights';
import ShareIcon from '@mui/icons-material/Share';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import GridViewIcon from '@mui/icons-material/GridView';
import HubIcon from '@mui/icons-material/Hub';
import { MenuItemLink, MenuProps, useSidebarState, useTranslate } from 'react-admin';
import BusinessIcon from '@mui/icons-material/Business';
import QueueIcon from '@mui/icons-material/Queue';
import CampaignIcon from '@mui/icons-material/Campaign';
import authProvider from '../auth/authProvider';
import {
  DASHBOARD_MODULE_ROLES,
  INQUIRIES_MODULE_ROLES,
  LEADS_MODULE_ROLES,
  WEBSITE_MODULE_ROLES,
  WHATSAPP_MODULE_ROLES,
} from '../roles/portalRoles';
import SubMenu from './SubMenu';

type MenuName = 'menuInbox' | 'menuDashboard' | 'menuHeroAffinity' | 'menuInquiry' | 'menuLeads';

const Menu = ({ dense = false }: MenuProps) => {
  const [state, setState] = useState({
    menuInbox: true,
    menuDashboard: true,
    menuHeroAffinity: true,
    menuInquiry: true,
    menuLeads: true,
  });
  const [open] = useSidebarState();
  const translate = useTranslate();
  const canAccessWhatsApp = authProvider.isRoleAuthorized(WHATSAPP_MODULE_ROLES);
  const canAccessInquiries = authProvider.isRoleAuthorized(INQUIRIES_MODULE_ROLES);
  const canAccessWebsite = authProvider.isRoleAuthorized(WEBSITE_MODULE_ROLES);
  const canAccessDashboard = authProvider.isRoleAuthorized(DASHBOARD_MODULE_ROLES);
  const canAccessLeads = authProvider.isRoleAuthorized(LEADS_MODULE_ROLES);
  const canAccessInbox = canAccessWhatsApp || canAccessWebsite;

  const handleToggle = (menu: MenuName) => {
    setState((current) => ({ ...current, [menu]: !current[menu] }));
  };

  return (
    <Box sx={{ width: open ? 220 : 50, marginTop: 1, transition: 'width 0.3s ease' }}>
      {canAccessDashboard && (
        <SubMenu
          handleToggle={() => handleToggle('menuDashboard')}
          isOpen={state.menuDashboard}
          name="pos.menu.dashboard"
          icon={<DashboardIcon />}
          dense={dense}
        >
          <MenuItemLink
            to="/dashboard/customers"
            state={{ _scrollToTop: true }}
            primaryText={translate('pos.menu.customers', { smart_count: 2 })}
            leftIcon={<PersonOutlineIcon />}
            dense={dense}
          />
          <MenuItemLink
            to="/dashboard/sessions"
            state={{ _scrollToTop: true }}
            primaryText="Sessions"
            leftIcon={<AccessTimeIcon />}
            dense={dense}
          />
          <MenuItemLink
            to="/dashboard/funnels"
            state={{ _scrollToTop: true }}
            primaryText="Funnels"
            leftIcon={<TimelineIcon />}
            dense={dense}
          />
          <MenuItemLink
            to="/dashboard/orders"
            state={{ _scrollToTop: true }}
            primaryText="Orders"
            leftIcon={<ShoppingCartIcon />}
            dense={dense}
          />
          <MenuItemLink
            to="/dashboard/cohorts-segmentation"
            state={{ _scrollToTop: true }}
            primaryText="Cohorts & Segmentation"
            leftIcon={<GridViewIcon />}
            dense={dense}
          />
          <MenuItemLink
            to="/dashboard/social"
            state={{ _scrollToTop: true }}
            primaryText="Social Campaigns"
            leftIcon={<ShareIcon />}
            dense={dense}
          />
          <MenuItemLink
            to="/dashboard/seller-acquisition"
            state={{ _scrollToTop: true }}
            primaryText="Marketplace sellers"
            leftIcon={<StorefrontIcon />}
            dense={dense}
          />
          <MenuItemLink
            to="/dashboard/apps"
            state={{ _scrollToTop: true }}
            primaryText="Apps"
            leftIcon={<PhoneIphoneIcon />}
            dense={dense}
          />
          <MenuItemLink
            to="/dashboard/links"
            state={{ _scrollToTop: true }}
            primaryText="Links"
            leftIcon={<LinkIcon />}
            dense={dense}
          />
          <MenuItemLink
            to="/dashboard/activities"
            state={{ _scrollToTop: true }}
            primaryText="Activities"
            leftIcon={<ActivityIcon />}
            dense={dense}
          />
          <MenuItemLink
            to="/dashboard/reprocessed"
            state={{ _scrollToTop: true }}
            primaryText="Reprocessed Listings"
            leftIcon={<RecyclingIcon />}
            dense={dense}
          />
          <MenuItemLink
            to="/dashboard/seo"
            state={{ _scrollToTop: true }}
            primaryText="SEO"
            leftIcon={<TravelExploreIcon />}
            dense={dense}
          />
          <MenuItemLink
            to="/dashboard/product-seo"
            state={{ _scrollToTop: true }}
            primaryText="Page SEO"
            leftIcon={<InsightsIcon />}
            dense={dense}
          />
        </SubMenu>
      )}

      {canAccessDashboard && (
        <SubMenu
          handleToggle={() => handleToggle('menuHeroAffinity')}
          isOpen={state.menuHeroAffinity}
          name="Hero products"
          icon={<HubIcon />}
          dense={dense}
        >
          <MenuItemLink
            to="/dashboard/product-affinity"
            state={{ _scrollToTop: true }}
            primaryText="Product affinity"
            leftIcon={<GridViewIcon />}
            dense={dense}
          />
        </SubMenu>
      )}

      {canAccessLeads && (
        <SubMenu
          handleToggle={() => handleToggle('menuLeads')}
          isOpen={state.menuLeads}
          name="Leads"
          icon={<BusinessIcon />}
          dense={dense}
        >
          <MenuItemLink
            to="/leadcompanies"
            state={{ _scrollToTop: true }}
            primaryText="Lead Companies"
            leftIcon={<BusinessIcon />}
            dense={dense}
          />
          <MenuItemLink
            to="/leadenrichmentjobs"
            state={{ _scrollToTop: true }}
            primaryText="Enrichment Queue"
            leftIcon={<QueueIcon />}
            dense={dense}
          />
          <MenuItemLink
            to="/marketlistingleads"
            state={{ _scrollToTop: true }}
            primaryText="Market listing leads"
            leftIcon={<RecyclingIcon />}
            dense={dense}
          />
        </SubMenu>
      )}

      {canAccessInbox && (
        <SubMenu
          handleToggle={() => handleToggle('menuInbox')}
          isOpen={state.menuInbox}
          name="menu.inbox"
          icon={<InboxIcon />}
          dense={dense}
        >
          {canAccessWhatsApp && (
            <>
              <MenuItemLink
                to="/messageinbox"
                primaryText={translate('menu.whatsapp')}
                leftIcon={<WhatsAppIcon />}
                dense={dense}
              />
              <MenuItemLink
                to="/dashboard/whatsapp"
                primaryText="WhatsApp Dashboard"
                leftIcon={<DashboardIcon />}
                dense={dense}
              />
              <MenuItemLink
                to="/whatsapp-broadcasts"
                primaryText="WhatsApp Broadcasts"
                leftIcon={<CampaignIcon />}
                dense={dense}
              />
            </>
          )}
          {canAccessWebsite && (
            <MenuItemLink
              to="/customercontacts"
              state={{ _scrollToTop: true }}
              primaryText={translate('menu.website')}
              leftIcon={<PublicIcon />}
              dense={dense}
            />
          )}
        </SubMenu>
      )}

      {canAccessInquiries && (
        <SubMenu
          handleToggle={() => handleToggle('menuInquiry')}
          isOpen={state.menuInquiry}
          name="menu.inquiry"
          icon={<QuestionAnswerIcon />}
          dense={dense}
        >
          <MenuItemLink
            to="/inquiry-inbox"
            state={{ _scrollToTop: true }}
            primaryText={translate('menu.inquiry_inbox')}
            leftIcon={<InboxIcon />}
            dense={dense}
          />
          <MenuItemLink
            to="/inquiries"
            state={{ _scrollToTop: true }}
            primaryText={translate('resources.inquiries.name', { smart_count: 2 })}
            leftIcon={<QuestionAnswerIcon />}
            dense={dense}
          />
        </SubMenu>
      )}
    </Box>
  );
};

export default Menu;
